import Vue from 'vue'
import axios from 'axios'
import {differenceInHours, parse} from 'date-fns'

import {parseTimeString} from '../util/util'
import * as TYPE from '../util/types'

import { deepCloneArray } from '~/utils/helper'

const eventState = {
	format: null,
	formatIndex: null,
	formatDescription: '',
	partialEventPk: null,
	approvalOption: 'instant_book',
	freeParkingText: null,
	streetParkingAddress: null,
	paidLotAddress: null,
	paidLotPrice: null,
	freeParking: false,
	streetParking: false,
	paidLot: false,
	visitedSubmit: false,
	confirmLocationUse: false,
	date: null,
	description: null,
	parkingInfo: null,
	endTime: null,
	location: null,
	locationInfo: [],
	locationInfoInputs: {},
	provisions: [],
	provisionsInputs: {},
	frequency: 'one-time',
	images: [],
	imageObjs: [],
	infoDialog: false,
	invalidSteps: [],
	notes: '',
	parentsOnly: false,
	price: 10,
	primaryImage: null,
	recurringFrequency: 'weekly',
	restrictionInfo: ['everyone'],
	restrictionInfoInputs: {},
	rules: null,
	seats: null,
	maxSeats: null,
	sponsors: [],
	startTime: null,
	tempSponsorDescription: null,
	tempSponsorImage: null,
	tempSponsorImageUrl: null,
	tempSponsorName: null,
	tempSponsorWebsite: null,
	subtitle: null,
	title: null,
	validSteps: [],
	wrkprtyType: null,
	addonTitle: null,
	addonDescription: null,
	addonPrice: null,
	addonTimeSlot: null,
	addonStartTime: null,
	addonEndTime: null,
	addonId: null
}

const locationState = {
	partialLocationPk: null,
	confirmLocationUse: false,
	type: null,
	title: null,
	sponsors: [],
	seats: null,
	description: '',
	parkingInfo: null,
	provisions: [],
	locationInfo: [],
	locationInfoInputs: {},
	rules: '',
	address: null,
	privateAddress: null,
	publicAddress: null,
	apartmentCheck: null,
	apartmentNumber: null,
	latitude: null,
	longitude: null,
	primaryImage: null,
	imageObjs: [],
	images: [],
	validSteps: [],
	invalidSteps: [],
	infoDialog: false,
	visitedSubmit: false
}

const hostState = {
	name: '',
	phone: '',
	address: '',
	newAddress: '',
	description: '',
	hostSpace: '',
	spaceIdea: '',
	infoDialog: false,
	apartmentNumber: null,
	apartmentCheck: false
}

const state = () => ({
	hostHelpOpen: false,
	lastHostApplyStep: null,
	partialEvents: [],
	partialLocations: [],
	event: deepCloneArray(eventState),
	host: deepCloneArray(hostState),
	location: deepCloneArray(locationState)
})

const mutations = {
	[TYPE.SET_HOST_HELP_OPEN]: (state, value) => {
		state.hostHelpOpen = value
	},
	[TYPE.SET_LAST_HOST_APPLY_STEP]: (state, value) => {
		state.lastHostApplyStep = value
	},
	[TYPE.SET_HOST_INFO_PROP]: (state, payload) => {
		Vue.set(state['host'], payload.prop, payload.value)
	},
	[TYPE.SET_LOCATION_PROP]: (state, payload) => {
		Vue.set(state['location'], payload.prop, payload.value)
	},
	[TYPE.SET_ADD_EVENT_PROP]: (state, payload) => {
		Vue.set(state.event, payload.prop, payload.value)
	},
	[TYPE.SET_ADD_LOCATION_IMAGES_URL]: (state, value) => {
		if (!state.location.imagesUrls.includes(value)) {
			state.location.imagesUrls.push(value)
		}
	},
	[TYPE.SET_ADD_EVENT_IMAGES_URL]: (state, value) => {
		if (!state.event.imagesUrls.includes(value)) {
			state.event.imagesUrls.push(value)
		}
	},
	[TYPE.SET_ADD_EVENT_VALID_STEP]: (state, value) => {
		if (!state.event.validSteps.includes(value)) {
			state.event.validSteps.push(value)
		}
	},
	[TYPE.REMOVE_ADD_EVENT_VALID_STEP]: (state, value) => {
		if (state.event.validSteps.includes(value)) {
			state.event.validSteps.splice(
				state.event.validSteps.findIndex(element => {
					return element === value
				}),
				1
			)
		}
	},
	[TYPE.SET_ADD_LOCATION_VALID_STEP]: (state, value) => {
		if (!state.location.validSteps.includes(value)) {
			state.location.validSteps.push(value)
		}
	},
	[TYPE.REMOVE_ADD_LOCATION_VALID_STEP]: (state, value) => {
		if (state.location.validSteps.includes(value)) {
			state.location.validSteps.splice(
				state.location.validSteps.findIndex(element => {
					return element === value
				}),
				1
			)
		}
	},
	[TYPE.SET_ADD_EVENT_INVALID_STEP]: (state, value) => {
		if (!state.event.invalidSteps.includes(value)) {
			state.event.invalidSteps.push(value)
		}
	},
	[TYPE.REMOVE_ADD_EVENT_INVALID_STEP]: (state, value) => {
		if (state.event.invalidSteps.includes(value)) {
			state.event.invalidSteps.splice(
				state.event.invalidSteps.findIndex(element => {
					return element === value
				}),
				1
			)
		}
	},
	[TYPE.SET_ADD_LOCATION_INVALID_STEP]: (state, value) => {
		if (!state.location.invalidSteps.includes(value)) {
			state.location.invalidSteps.push(value)
		}
	},
	[TYPE.REMOVE_ADD_LOCATION_INVALID_STEP]: (state, value) => {
		if (state.location.invalidSteps.includes(value)) {
			state.location.invalidSteps.splice(
				state.location.invalidSteps.findIndex(element => {
					return element === value
				}),
				1
			)
		}
	},
	[TYPE.ADD_EVENT_RESET_STATE]: state => {
		Vue.set(state, 'event', deepCloneArray(eventState))
	},
	[TYPE.ADD_LOCATION_RESET_STATE]: state => {
		Vue.set(state, 'location', deepCloneArray(locationState))
	},
	[TYPE.SET_ADD_EVENT_STATE]: (state, payload) => {
		Vue.set(state, 'event', payload)
	},
	[TYPE.SET_PARTIAL_EVENTS]: (state, payload) => {
		Vue.set(state, 'partialEvents', payload)
	},
	[TYPE.REMOVE_PARTIAL_EVENT]: (state, value) => {
		let e = deepCloneArray(state.partialEvents)
		e.splice(
			e.indexOf(
				e.find(obj => {
					return obj.state.partialEventPk === value
				})
			),
			1
		)
		Vue.set(state, 'partialEvents', e)
	},
	[TYPE.SET_ADD_LOCATION_STATE]: (state, payload) => {
		Vue.set(state, 'location', payload)
	},
	[TYPE.SET_PARTIAL_LOCATIONS]: (state, payload) => {
		Vue.set(state, 'partialLocations', payload)
	},
	[TYPE.REMOVE_PARTIAL_LOCATION]: (state, value) => {
		let e = deepCloneArray(state.partialLocations)
		e.splice(
			e.indexOf(
				e.find(obj => {
					return obj.state.partialLocationPk === value
				})
			),
			1
		)
		Vue.set(state, 'partialLocations', e)
	}
}

const actions = {
	fetchAllPartialEvents({ state, getters, commit }) {
		axios
			.get('/api/v1/partial-event/')
			.then(response => {
				commit(TYPE.SET_PARTIAL_EVENTS, response.data)
			})
			.catch(error => {
				console.log(error)
			})
	},
	loadPartialEvent({ state, getters, commit }, pk) {
		let partial = axios
			.get('/api/v1/partial-event/' + pk + '/')
			.then(response => {
				commit(TYPE.SET_ADD_EVENT_STATE, response.data.state)
				commit(TYPE.SET_ADD_EVENT_PROP, { prop: 'partialEventPk', value: pk })
			})
			.catch(error => {
				console.log(error)
			})
	},
	startPartialEvent({ state, getters, commit }) {
		let data = { state: deepCloneArray(state.event), host: getters.getHost.pk }
		axios
			.post('/api/v1/partial-event/', data)
			.then(response => {
				commit(TYPE.SET_ADD_EVENT_PROP, {
					prop: 'partialEventPk',
					value: response.data.id
				})
				data = { state: deepCloneArray(state.event), host: getters.getHost.pk }
				axios
					.put('/api/v1/partial-event/' + response.data.id + '/', data)
					.then(response => {})
					.catch(error => {
						console.log(error)
					})
			})
			.catch(error => {
				console.log(error)
			})
	},
	updatePartialEvent({ state, getters, commit }) {
		let data = { state: deepCloneArray(state.event) }
		axios
			.put('/api/v1/partial-event/' + state.event.partialEventPk + '/', data)
			.then(response => {})
			.catch(error => {
				console.log(error)
			})
	},
	deletePartialEvent({ state, getters, commit }, pk) {
		axios
			.delete('/api/v1/partial-event/' + pk + '/')
			.then(response => {
				commit(TYPE.REMOVE_PARTIAL_EVENT, pk)
			})
			.catch(error => {
				console.log(error)
			})
	},
	// locations below
	fetchAllPartialLocations({ state, getters, commit }) {
		console.log('fetchAllPartialLocations')
		axios
			.get('/api/v1/partial-location/')
			.then(response => {
				commit(TYPE.SET_PARTIAL_LOCATIONS, response.data)
			})
			.catch(error => {
				console.log(error)
			})
	},
	loadPartialLocation({ state, getters, commit }, pk) {
		let partial = axios
			.get('/api/v1/partial-location/' + pk + '/')
			.then(response => {
				commit(TYPE.SET_ADD_LOCATION_STATE, response.data.state)
				commit(TYPE.SET_LOCATION_PROP, { prop: 'partialLocationPk', value: pk })
			})
			.catch(error => {
				console.log(error)
			})
	},
	startPartialLocation({ state, getters, commit }) {
		let data = {
			state: deepCloneArray(state.location),
			host: getters.getHost.pk
		}
		axios
			.post('/api/v1/partial-location/', data)
			.then(response => {
				commit(TYPE.SET_LOCATION_PROP, {
					prop: 'partialLocationPk',
					value: response.data.id
				})
				data = {
					state: deepCloneArray(state.location),
					host: getters.getHost.pk
				}
				axios
					.put('/api/v1/partial-location/' + response.data.id + '/', data)
					.then(response => {})
					.catch(error => {
						console.log(error)
					})
			})
			.catch(error => {
				console.log(error)
			})
	},
	updatePartialLocation({ state, getters, commit }) {
		let data = { state: deepCloneArray(state.location) }
		axios
			.put(
				'/api/v1/partial-location/' + state.location.partialLocationPk + '/',
				data
			)
			.then(response => {})
			.catch(error => {
				console.log(error)
			})
	},
	deletePartialLocation({ state, getters, commit }, pk) {
		axios
			.delete('/api/v1/partial-location/' + pk + '/')
			.then(response => {
				commit(TYPE.REMOVE_PARTIAL_LOCATION, pk)
			})
			.catch(error => {
				console.log(error)
			})
	},
	saveAddonTemplate({ state, getters, commit }) {
		let data = {}
		data.title = state.event.addonTitle
		if (data.title === null) {
			return
		}
		data.description = state.event.addonDescription
		if (data.description === null) {
			return
		}
		data.price = parseFloat(state.event.addonPrice)
		if (data.price === null) {
			return
		}
		data.duration = parseFloat(state.event.addonDuration)
		if (data.duration === null) {
			return
		}
		data.user = getters.user.pk
		if (data.user === null) {
			return
		}
		data.approved = true

		return axios
			.post('/api/v1/event-add-ons/', data)
			.then(response => {
				commit(TYPE.SET_ADD_EVENT_PROP, {
					prop: 'addonId',
					value: response.data.id
				})
			})
			.catch(error => {
				console.log(error)
			})
	},
	setHostHelpOpen({ commit }, value) {
		commit(TYPE.SET_HOST_HELP_OPEN, value)
	},
	setlastHostApplyStep({ commit }, value) {
		commit(TYPE.SET_LAST_HOST_APPLY_STEP, value)
	},
	addEventResetState({ commit }) {
		commit(TYPE.ADD_EVENT_RESET_STATE)
	},
	addLocationResetState({ commit }) {
		commit(TYPE.ADD_LOCATION_RESET_STATE)
	},
	serverPostHostApplication({ commit, state, getters }) {
		let data = {
			user: getters.user.pk,
			phone: state.host.phone,
			description: state.host.description,
			address: state.host.address,
			new_address: state.host.newAddress,
			hostSpace: state.host.hostSpace,
			spaceIdea: state.host.spaceIdea
		}
		if (state.host.apartmentCheck) {
			data.address += ' #' + String(state.host.apartmentNumber)
			data.new_address.apartmentNumber = state.host.apartmentNumber
		}
		return axios
			.post('/api/v1/hostrequest/', data)
			.then(response => {
				commit(TYPE.SET_HOST, response.data)
				return response
			})
			.catch(error => {
				console.error(error)
				return error
			})
	},
	serverPutHostApplication({ commit, state, getters }, pk) {
		let data = {
			user: getters.user.pk,
			phone: state.host.phone,
			description: state.host.description,
			address: state.host.address,
			hostSpace: state.host.hostSpace,
			spaceIdea: state.host.spaceIdea
		}
		axios
			.put('/api/v1/hostrequest/' + pk + '/', data)
			.then(response => {
				commit(TYPE.SET_HOST, response.data)
				return response
			})
			.catch(error => {
				console.error(error)
			})
	},
	setHostApplyInfo({ commit }, payload) {
		commit(TYPE.SET_HOST_INFO_PROP, payload)
	},
	setAddLocationProp({ commit }, payload) {
		commit(TYPE.SET_LOCATION_PROP, payload)
	},
	addLocationProcessFiles({ commit, state }, payload) {
		const files = payload
		let images = []
		for (var i = 0; i < files.length; i++) {
			let filename = files[i].name
			let fileReader = new FileReader()
			fileReader.addEventListener('load', event => {
				commit(TYPE.SET_ADD_LOCATION_IMAGES_URL, fileReader.result)
			})
			if (filename.lastIndexOf('.') <= 0) {
				return alert('Please select a valid file')
			}
			fileReader.readAsDataURL(files[i])
			images.push(files[i])
		}

		// Sort images so they are in the same order as preview thumbnails
		images.sort(function(a, b) {
			return a.size - b.size
		})

		commit(TYPE.SET_LOCATION_PROP, { prop: 'images', value: images })
	},
	addLocationProcessFilesSponsor({ commit, state }, payload) {
		const file = payload
		let image = []
		for (var i = 0; i < file.length; i++) {
			let filename = file[i].name
			let fileReader = new FileReader()
			fileReader.addEventListener('load', event => {
				commit(TYPE.SET_ADD_LOCATION_IMAGES_URL, fileReader.result)
			})
			if (filename.lastIndexOf('.') <= 0) {
				return alert('Please select a valid file')
			}
			fileReader.readAsDataURL(file[i])
			image.push(file[i])
		}

		// Sort images so they are in the same order as preview thumbnails
		image.sort(function(a, b) {
			return a.size - b.size
		})

		commit(TYPE.SET_LOCATION_PROP, { prop: 'tempSponsorImage', value: image })
	},
	setAddEventProp({ commit }, payload) {
		commit(TYPE.SET_ADD_EVENT_PROP, payload)
	},
	setAddEventValidStep({ commit }, value) {
		commit(TYPE.SET_ADD_EVENT_VALID_STEP, value)
	},
	removeAddEventValidStep({ commit }, value) {
		commit(TYPE.REMOVE_ADD_EVENT_VALID_STEP, value)
	},
	setAddLocationValidStep({ commit }, value) {
		commit(TYPE.SET_ADD_LOCATION_VALID_STEP, value)
	},
	removeAddLocationValidStep({ commit }, value) {
		commit(TYPE.REMOVE_ADD_LOCATION_VALID_STEP, value)
	},
	setAddEventInvalidStep({ commit }, value) {
		commit(TYPE.SET_ADD_EVENT_INVALID_STEP, value)
	},
	removeAddEventInvalidStep({ commit }, value) {
		commit(TYPE.REMOVE_ADD_EVENT_INVALID_STEP, value)
	},
	setAddLocationInvalidStep({ commit }, value) {
		commit(TYPE.SET_ADD_LOCATION_INVALID_STEP, value)
	},
	removeAddLocationInvalidStep({ commit }, value) {
		commit(TYPE.REMOVE_ADD_LOCATION_INVALID_STEP, value)
	},
	addEventProcessFiles({ commit, state }, payload) {
		const files = payload
		let images = []
		for (var i = 0; i < files.length; i++) {
			let filename = files[i].name
			let fileReader = new FileReader()
			fileReader.addEventListener('load', event => {
				commit(TYPE.SET_ADD_EVENT_IMAGES_URL, fileReader.result)
			})
			if (filename.lastIndexOf('.') <= 0) {
				return alert('Please select a valid file')
			}
			fileReader.readAsDataURL(files[i])
			images.push(files[i])
		}

		// Sort images so they are in the same order as preview thumbnails
		images.sort(function(a, b) {
			return a.size - b.size
		})

		commit(TYPE.SET_ADD_EVENT_PROP, { prop: 'images', value: images })
	}
}

const getters = {
	getHostProp: state => prop => state[prop],
	getHostHelpOpen: state => state.hostHelpOpen,
	getlastHostApplyStep: state => state.lastHostApplyStep,
	getHostApplyInfo: state => prop => state.host[prop],
	getAddLocationProp: state => prop => {
		return state.location[prop]
	},
	getAddEventProp: state => prop => {
		return state.event[prop]
	},
	getAddonValid: state => {
		return (
			state.event['addonTitle'] &&
			state.event['addonDescription'] &&
			state.event['addonStartTime'] &&
			state.event['addonEndTime'] &&
			state.event['addonPrice'] &&
			parseFloat(state.event['addonPrice']) >= 0 &&
			state.event['addonTimeSlot']
		)
	},
	getAddLocationReadyToSubmit: state => {
		let isReady = (
			state.location['type'] &&
			state.location['title'] &&
			state.location['description'] &&
			state.location['privateAddress'] &&
			state.location['seats'] &&
			state.location['invalidSteps'].length === 0 &&
			state.location['seats'] >= 5 &&
			state.location['images'].length > 0 &&
			state.location['primaryImage']
		)
		return isReady
	},
	getEventDuration: state => {
		let timeError = false
		let startHour = null
		let endHour = null
		if (state.event['endTime'] !== null && state.event['startTime'] !== null) {
			startHour = parseInt(state.event['startTime'].split(':')[0])
			if (
				state.event['startTime'][state.event['startTime'].length - 2] === 'p'
			) {
				if (startHour !== 12) {
					startHour += 12
				}
			}
			endHour = parseInt(state.event['endTime'].split(':')[0])
			if (state.event['endTime'][state.event['endTime'].length - 2] === 'p') {
				if (endHour !== 12) {
					endHour += 12
				}
			}
			let startMinute = parseInt(state.event['startTime'].split(':')[1].substring(0,2))
			let endMinute = parseInt(state.event['endTime'].split(':')[1].substring(0,2))
			startHour += (startMinute / 60)
			endHour += (endMinute / 60)

			return endHour - startHour
		}
		return null
	},
	getAddEventReadyToSubmit: state => {
		let timeError = false
		if (state.event['endTime'] !== null && state.event['startTime'] !== null) {
			let timeStart = parse(state.event['startTime'], 'yyyy-MM-ddTHH:mm:ssxxx', new Date())
			let timeEnd = parse(state.event['endTime'], 'yyyy-MM-ddTHH:mm:ssxxxxx', new Date())
			let eventLengthHours = differenceInHours(timeEnd, timeStart)
			if (state.event['wrkprtyType'] === 'wrkprty') {
				if (eventLengthHours < 4) {
					timeError = true
				}
			} else {
				if (eventLengthHours < 1) {
					timeError = true
				}
			}
		} else {
			timeError = true
		}
		let addonValid = true
		console.log("timeerror", timeError)
		if (
			state.event['addonTitle'] !== null ||
			state.event['addonDescription'] !== null ||
			state.event['addonStartTime'] !== null ||
			state.event['addonEndTime'] !== null ||
			state.event['addonPrice'] !== null ||
			state.event['addonTimeSlot'] !== null
		) {
			addonValid =
				state.event['addonTitle'] !== null &&
				state.event['addonDescription'] !== null &&
				state.event['addonStartTime'] !== null &&
				state.event['addonEndTime'] !== null &&
				state.event['addonPrice'] !== null &&
				parseFloat(state.event['addonPrice']) >= 0 &&
				state.event['addonTimeSlot'] !== null
		}
		let isReady = !(
			state.event['wrkprtyType'] === null ||
			state.event['title'] === null ||
			state.event['location'] === null ||
			state.event['description'] === null ||
			state.event['date'] === null ||
			state.event['startTime'] === null ||
			state.event['endTime'] === null ||
			timeError ||
			!addonValid ||
			// state.event['format'] === null ||
			state.event['seats'] === null ||
			state.event['price'] === null ||
			state.event['seats'] < 5 ||
			state.event['primaryImage'] === null ||
			state.event['seats'] > state.event['maxSeats'] ||
			state.event['images'].length < 1 ||
			state.event['privateAddress'] === null
		)
		return isReady
	},
	getAddEventStateEmpty: state => {
		if (JSON.stringify(state.event) === JSON.stringify(eventState)) {
			return true
		} else {
			return false
		}
	},
	getAddLocationStateEmpty: state => {
		if (JSON.stringify(state.location) === JSON.stringify(locationState)) {
			return true
		} else {
			return false
		}
	},
	getHostProcessStep: (state, rootState) => {
		if (rootState.getHost) {
			// getMyLocations
			if (
				rootState.getMyLocations.length !== 0 ||
				rootState.getHost.hostSpace === 'noSpace' ||
				rootState.getHost.hostSpace === 'spaceIdea'
			) {
				return 4
			} else if (rootState.getHost.hostSpace !== '') {
				return 3
			} else if (rootState.getHost.phone !== '') {
				return 2
			} else {
				return 1
			}
		}
	}
}

export default {
	state,
	getters,
	mutations,
	actions
}
